import React, { useState } from 'react';
import { financialTransactionAPI } from '../financialTransactionService';
import toast from 'react-hot-toast';
import { Receipt, ChevronDown, ArrowRight, Plus, Loader, Trash2, ExternalLink, Pencil, ShoppingBag, Heart } from 'lucide-react';
import { btn } from '../../utils/buttonStyles';

const CURRENT_YEAR = new Date().getFullYear();

const TransactionRow = ({
  txn,
  expanded,
  onToggle,
  isAdmin,
  currentUserId,
  onEdit,
  onSettleUp,
  confirmDeleteId,
  deletingId,
  onConfirmDelete,
  onCancelDelete,
  onDelete,
  entityId,
  navigate,
  theme,
}) => {
  const isSettlement = txn.type === 'poker_settlement';
  const canEdit = !isSettlement && (isAdmin || String(txn.createdBy) === String(currentUserId));
  const statusColor  =
    txn.status === 'completed'         ? 'text-green-500 bg-green-500/10' :
    txn.status === 'partial completed' ? 'text-orange-500 bg-orange-500/10' :
                                         `${theme.textSecondary} ${theme.backgroundSecondary}`;

  return (
    <div className="overflow-hidden">
      <div className="flex items-center gap-2 pr-3">
        <button
          className="flex items-center gap-3 flex-1 min-w-0 px-4 py-3 text-left"
          onClick={() => onToggle(txn.id)}
        >
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isSettlement ? 'bg-violet-500/15' : 'bg-blue-500/15'}`}>
            <Receipt className={`w-4 h-4 ${isSettlement ? 'text-violet-500' : 'text-blue-500'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold ${theme.text} truncate`}>{txn.title}</p>
            <p className={`text-xs ${theme.textSecondary}`}>
              {new Date(txn.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              {' · '}
              {isSettlement ? 'Settlement' : 'Expense'}
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className={`text-sm font-bold ${theme.text}`}>${txn.totalAmount.toFixed(0)}</p>
            <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full capitalize ${statusColor}`}>
              {txn.status}
            </span>
          </div>
          <ChevronDown className={`w-4 h-4 ${theme.textSecondary} flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>

        {(canEdit || isAdmin) && (
          confirmDeleteId === txn.id ? (
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                onClick={() => onDelete(txn.id)}
                disabled={deletingId === txn.id}
                className="px-2.5 py-1.5 rounded-lg bg-red-500 text-white text-xs font-semibold disabled:opacity-50 flex items-center gap-1"
              >
                {deletingId === txn.id && <Loader className="w-3 h-3 animate-spin" />}
                Delete
              </button>
              <button
                onClick={onCancelDelete}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold ${theme.buttonSecondary}`}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1 flex-shrink-0">
              {canEdit && (
                <button
                  onClick={() => onEdit(txn)}
                  className={`p-1.5 rounded-lg ${theme.textSecondary} hover:text-blue-500 hover:bg-blue-500/10 transition-colors`}
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => onConfirmDelete(txn.id)}
                  title="Delete expense"
                  className={`p-1.5 rounded-lg ${theme.textSecondary} hover:text-red-500 hover:bg-red-500/10 transition-colors`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          )
        )}
      </div>

      {expanded && txn.creditTransactions?.length > 0 && (
        <div className={`px-4 pb-3 pt-1 border-t ${theme.borderSecondary} space-y-1.5`}>
          <div className="flex items-center justify-between mb-2">
            <p className={`text-xs font-semibold uppercase tracking-wide ${theme.textSecondary}`}>Breakdown</p>
            {isSettlement && txn.eventId && (
              <button
                onClick={() => navigate(`/squads/${entityId}?tab=past-games&eventId=${txn.eventId}`)}
                className="flex items-center gap-1 text-xs font-semibold text-blue-500 hover:text-blue-400 transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                View Game
              </button>
            )}
          </div>
          {txn.creditTransactions.map((ct, i) => {
            const isPaid      = ct.status === 'paid';
            const isPartial   = ct.status === 'partial';
            const paidAmt     = ct.paidAmount ?? parseFloat(ct.paid_amount || '0');
            const remaining   = Math.max(0, ct.amount - paidAmt);
            const isMyRow     = String(ct.userId) === String(currentUserId) && !isPaid;
            return (
              <div key={i} className="flex items-center justify-between gap-2">
                <div className={`flex items-center gap-1.5 text-sm ${theme.text} min-w-0`}>
                  <span className="font-medium truncate">{ct.user?.firstName || 'Anonymous'}</span>
                  <ArrowRight className={`w-3 h-3 flex-shrink-0 ${theme.textSecondary}`} />
                  <span className="font-medium truncate">{ct.toUser?.firstName || (ct.toUserId == null ? 'Pool' : '?')}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-sm font-semibold ${isPaid ? theme.textSecondary : theme.text}`}>
                    ${isPaid ? ct.amount.toFixed(0) : remaining.toFixed(0)}
                  </span>
                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                    isPaid    ? 'bg-green-500/15 text-green-500' :
                    isPartial ? 'bg-orange-500/15 text-orange-500' :
                                `${theme.backgroundSecondary} ${theme.textSecondary}`
                  }`}>
                    {isPaid ? 'Paid' : isPartial ? 'Partial' : 'Pending'}
                  </span>
                  {isMyRow && onSettleUp && (
                    <button
                      type="button"
                      onClick={() => onSettleUp({
                        creditorName: ct.toUser?.firstName || 'Group',
                        amount: remaining,
                        creditTransactions: [{ creditTransactionId: ct.id, amount: remaining }],
                      })}
                      className={`${btn.base} ${btn.sm} ${theme.buttonPrimary}`}
                    >
                      Pay
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * TransactionSummaryComponent
 *
 * Owns:
 *   - expandedTransactions, expandedTransactionYears state
 *   - deletingId, confirmDeleteId state
 *   - handleDeleteTransaction logic
 *
 * Props:
 *   displayTransactions, groupedTransactions – data from parent
 *   isAdmin, isMember                        – permissions
 *   onAddExpense                             – open AddExpense modal
 *   onDeleted                                – called after a successful delete (refresh)
 *   loadingTransactions                      – loading spinner flag
 *   entityId, navigate                       – for View Game link
 *   theme
 */
const TransactionSummaryComponent = ({
  displayTransactions,
  groupedTransactions,
  isAdmin,
  isMember,
  currentUserId,
  onAddContribution,
  onAddExpense,
  onAddDonation,
  onEdit,
  onSettleUp,
  onDeleted,
  loadingTransactions,
  entityId,
  navigate,
  theme,
}) => {
  const [expandedTransactions, setExpandedTransactions]         = useState({});
  const [expandedTransactionYears, setExpandedTransactionYears] = useState({ [CURRENT_YEAR]: true });
  const [deletingId, setDeletingId]                             = useState(null);
  const [confirmDeleteId, setConfirmDeleteId]                   = useState(null);

  const handleDeleteTransaction = async (txnId) => {
    setDeletingId(txnId);
    try {
      await financialTransactionAPI.deleteTransaction(txnId);
      toast.success('Expense deleted');
      setConfirmDeleteId(null);
      onDeleted?.();
    } catch (err) {
      toast.error(err.message || 'Failed to delete expense');
    } finally {
      setDeletingId(null);
    }
  };

  const toggleTransaction     = (txnId) => setExpandedTransactions(p => ({ ...p, [txnId]: !p[txnId] }));
  const toggleTransactionYear = (year)  => setExpandedTransactionYears(p => ({ ...p, [year]: !p[year] }));

  return (
    <div className={`${theme.card} rounded-2xl border ${theme.border} overflow-hidden`}>
      <div className="px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Receipt className={`w-4 h-4 ${theme.textSecondary}`} />
          <h3 className={`font-semibold ${theme.text}`}>Transactions</h3>
          {displayTransactions.length > 0 && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${theme.backgroundSecondary} ${theme.textSecondary}`}>
              {displayTransactions.length}
            </span>
          )}
          {loadingTransactions && <Loader className="w-3.5 h-3.5 text-blue-500 animate-spin" />}
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <>
              <button
                onClick={onAddDonation}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-pink-500/15 text-pink-500 hover:bg-pink-500/25 transition-colors"
              >
                <Heart className="w-4 h-4" />
                Donation
              </button>
              <button
                onClick={onAddExpense}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-orange-500/15 text-orange-500 hover:bg-orange-500/25 transition-colors"
              >
                <ShoppingBag className="w-4 h-4" />
                Add Expense
              </button>
            </>
          )}
          {isMember && (
            <button
              onClick={onAddContribution}
              className={`flex items-center gap-1.5 ${btn.base} ${btn.sm} ${theme.buttonPrimary}`}
            >
              <Plus className="w-4 h-4" />
              Add Contribution
            </button>
          )}
        </div>
      </div>

      {displayTransactions.length === 0 ? (
        <div className="px-5 pb-8 flex flex-col items-center gap-3">
          <div className={`w-12 h-12 rounded-2xl ${theme.backgroundSecondary} flex items-center justify-center`}>
            <Receipt className={`w-6 h-6 ${theme.textSecondary}`} />
          </div>
          <p className={`text-sm ${theme.textSecondary}`}>No transactions yet</p>
          {isMember && (
            <button
              onClick={onAddContribution}
              className={`${btn.base} ${btn.md} ${theme.buttonPrimary}`}
            >
              Add First Contribution
            </button>
          )}
        </div>
      ) : groupedTransactions ? (
        <div>
          {groupedTransactions.map(({ year, seasonGroups, totalCount }) => {
            const isExpanded = !!expandedTransactionYears[year];
            return (
              <div key={year} className={`border-t ${theme.borderSecondary}`}>
                <button
                  type="button"
                  onClick={() => toggleTransactionYear(year)}
                  className={`w-full flex items-center justify-between px-5 py-3 text-left transition-colors ${theme.cardHover}`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold ${theme.text}`}>{year}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${theme.backgroundSecondary} ${theme.textSecondary}`}>
                      {totalCount} {totalCount === 1 ? 'Transaction' : 'Transactions'}
                    </span>
                  </div>
                  <ChevronDown className={`w-4 h-4 ${theme.textSecondary} transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>

                {isExpanded && seasonGroups.map(({ season, txns }) => (
                  <div key={season}>
                    {seasonGroups.length > 1 && (
                      <div className={`px-5 py-1.5 ${theme.backgroundSecondary} border-t ${theme.borderSecondary}`}>
                        <p className={`text-xs font-semibold uppercase tracking-wide ${theme.textSecondary}`}>{season}</p>
                      </div>
                    )}
                    <div className={`divide-y ${theme.borderSecondary} border-t ${theme.borderSecondary}`}>
                      {txns.map(txn => (
                        <TransactionRow
                          key={txn.id}
                          txn={txn}
                          expanded={!!expandedTransactions[txn.id]}
                          onToggle={toggleTransaction}
                          isAdmin={isAdmin}
                          currentUserId={currentUserId}
                          onEdit={onEdit}
                          onSettleUp={onSettleUp}
                          confirmDeleteId={confirmDeleteId}
                          deletingId={deletingId}
                          onConfirmDelete={setConfirmDeleteId}
                          onCancelDelete={() => setConfirmDeleteId(null)}
                          onDelete={handleDeleteTransaction}
                          entityId={entityId}
                          navigate={navigate}
                          theme={theme}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ) : (
        <div className={`divide-y ${theme.borderSecondary}`}>
          {displayTransactions.map(txn => (
            <TransactionRow
              key={txn.id}
              txn={txn}
              expanded={!!expandedTransactions[txn.id]}
              onToggle={toggleTransaction}
              isAdmin={isAdmin}
              currentUserId={currentUserId}
              onEdit={onEdit}
              confirmDeleteId={confirmDeleteId}
              deletingId={deletingId}
              onConfirmDelete={setConfirmDeleteId}
              onCancelDelete={() => setConfirmDeleteId(null)}
              onDelete={handleDeleteTransaction}
              entityId={entityId}
              navigate={navigate}
              theme={theme}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TransactionSummaryComponent;
